import { Empty } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { CustomCommentBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    CustomViewConfig,
    FilterCommonCondition,
    RecordLikeProtocol,
    Sorter,
    ViewControlHorizontalScrollToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import { DataSourceType, VariableType } from '@lighthouse/core'
import {
    findRecordInPool,
    getCursorStyle,
    getPaddingStyle,
    getPrimaryDsSystemFieldId,
    getWithScopeIdAndRecordIdAndColumnId,
    pageStackPubSub,
    scroll2EndLeftOrRight,
    scroll2ViewItem,
    scrollToRecord,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useRegisterViewControlListeners
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { useStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import {
    fetchViewRecordsAtom,
    loadCommentViewRecordsAtom,
    loadCommentViewReplayCountAtom,
    loadViewRecordsAtom
} from '@/atoms/blockRecordsDict/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { CustomControllerProps, FetchViewDataPayload } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const CustomCommentController: React.FC<CustomControllerProps> = ({ blockData, node, firstRef, onReady, children, ...rest }) => {
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        linkFilterController,
        canSort,
        canSearch,
        pagination,
        breakPoint: baseBreakpoint,
        filter,
        cursor,
        sorts,

        commentConfig
    } = config as CustomViewConfig

    const envId = useCurrentEnvId()
    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id)

    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)
    const { schema, viewOptions, type: dsType } = dataSource || {}

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    // 视图独立数据hook
    const {
        currentPage = 1,
        rowTotal,
        recordPoints,
        setViewIndependentData
    } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })

    const handleRenderRichText = useRenderRichText(baseBreakpoint.emptyView?.title)

    const mergedPagination = useMemo(() => ({ ...pagination, currentPage, rowTotal }), [pagination, currentPage, rowTotal])

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    // 首批数据加载完成且渲染完成后调用 onReady
    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            // 双重 requestAnimationFrame 确保 DOM 渲染完成
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { run: loadMoreRecords } = useAtomAsyncAction(loadViewRecordsAtom)
    const { run: loadCommentViewReplayCount } = useAtomAsyncAction(loadCommentViewReplayCountAtom)
    const { run: loadCommentViewRecords } = useAtomAsyncAction(loadCommentViewRecordsAtom)
    const { ref, breakPoint } = useBreakpoint([!!fetchViewRecordsValue])

    const { pageSize = 10 } = pagination

    const getTopReplayFilter = useCallback(() => {
        if (!commentConfig?.rootId || !commentConfig.parentId) {
            return
        }

        return [
            {
                idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: commentConfig.rootId } },
                operator: 'isEmpty'
            }
            // {
            //     idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: commentConfig.parentId } },
            //     operator: 'isEmpty'
            // }
        ] as FilterCommonCondition[]
    }, [commentConfig?.parentId, commentConfig?.rootId])

    const getFilterByRootId = useCallback(
        (rootId: string) => {
            if (!commentConfig?.rootId) {
                return
            }

            return [
                {
                    idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: commentConfig.rootId } },
                    operator: '=',
                    paramList: [{ type: VariableType.VALUE, valueVariable: { value: rootId, type: 'text' } }]
                }
            ] as FilterCommonCondition[]
        },
        [commentConfig?.rootId]
    )

    const [replayCountMap, setReplayCountMap] = useImmer<Record<string, number>>({})

    // 使用新的依赖管理方案，优化重新渲染（仅包含对象/数组类型字段）
    const stableDeps = useFetchViewDataDeps({
        resolvedFilter,
        resolvedLinkFilter,
        sorts: [{ fieldId: 'createdTime', id: '', order: 'DESC' }, ...(sorts || [])]
    })

    const idFieldId = useMemo(
        () =>
            (schema && viewOptions && dsType === DataSourceType.joinDataSource
                ? getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType)
                : 'ID') ?? 'ID',
        [dsType, schema, viewOptions]
    )

    const [enforceUpdateKey, setEnforceUpdateKey] = useState(0)

    const fetchViewData = useCallback(
        async (params: FetchViewDataPayload) => {
            const { pageNum } = params
            if (pointer) {
                const topReplayFilters = getTopReplayFilter()
                if (!topReplayFilters) {
                    return
                }
                const quickFilters: FilterCommonCondition[] = [...topReplayFilters]
                const payload = {
                    stackId,
                    pageId,
                    viewId: id,
                    uniqueId,
                    scope,
                    isSyncComponent: !!scope,
                    pagination: { currentPage: pageNum, pageSize },
                    params: {
                        quickFilters,
                        sorts: stableDeps.sorts,
                        linkFilter: stableDeps.resolvedLinkFilter,
                        filter: stableDeps.resolvedFilter
                    }
                }
                const res = await mutation(payload)
                if (!res) {
                    return
                }

                // 解决重新加载父级评论列表时，mutation把子级的records重新覆盖了，导致子级数据丢失的问题，这里让子级卸载重新初始化
                if (!firstRef.current && pageNum === 1) {
                    setEnforceUpdateKey(s => s + 1)
                }

                const tasks = await Promise.allSettled(
                    res.records.map(r => {
                        const rootId = r.content[idFieldId]?.value?.toString()
                        const quickFilterWithRootId = rootId && getFilterByRootId(rootId)
                        if (!quickFilterWithRootId) {
                            throw new Error('find quickFilterWithRootId failed')
                        }
                        const quickFilters: FilterCommonCondition[] = [...quickFilterWithRootId]
                        const replayCountPayload = {
                            stackId,
                            pageId,
                            viewId: id,
                            uniqueId,
                            scope,
                            isSyncComponent: !!scope,
                            pagination: { currentPage: pageNum, pageSize },
                            params: {
                                filter: stableDeps.resolvedFilter,
                                quickFilters,
                                sorts: stableDeps.sorts,
                                linkFilter: stableDeps.resolvedLinkFilter
                            }
                        }
                        return loadCommentViewReplayCount(replayCountPayload).then(res => (res ? { id: r.id, count: res } : undefined))
                    })
                )
                setReplayCountMap(draft => {
                    tasks.forEach(item => {
                        if (item.status === 'fulfilled' && item.value) {
                            draft[item.value.id] = item.value.count
                        }
                    })
                })
            }
        },
        [
            pointer,
            getTopReplayFilter,
            stackId,
            pageId,
            id,
            uniqueId,
            scope,
            pageSize,
            stableDeps.sorts,
            stableDeps.resolvedLinkFilter,
            stableDeps.resolvedFilter,
            mutation,
            firstRef,
            setReplayCountMap,
            idFieldId,
            getFilterByRootId,
            loadCommentViewReplayCount
        ]
    )

    // 获取回复评论
    const fetchReplayData = useCallback(
        async (payload: { pageNum: number; pageSize: number; rootId: string }) => {
            const { pageNum, pageSize, rootId } = payload
            const quickFilterWithRootId = getFilterByRootId(rootId)
            if (!quickFilterWithRootId) {
                return
            }
            const quickFilters = [...quickFilterWithRootId]
            const sorts: Sorter[] = [{ fieldId: 'createdTime', id: '', order: 'ASC' }]

            await loadCommentViewRecords({
                stackId,
                pageId,
                viewId: id,
                uniqueId,
                scope,
                isSyncComponent: !!scope,
                pagination: { currentPage: pageNum, pageSize },
                params: {
                    quickFilters,
                    sorts,
                    linkFilter: stableDeps.resolvedLinkFilter,
                    filter: stableDeps.resolvedFilter
                }
            })
        },
        [
            getFilterByRootId,
            loadCommentViewRecords,
            stackId,
            pageId,
            id,
            uniqueId,
            scope,
            stableDeps.resolvedLinkFilter,
            stableDeps.resolvedFilter
        ]
    )

    // 加载一级更多评论
    const handleLoadMoreRecords = useCallback(
        async (pageNum: number) => {
            if (rowTotal && pageNum > Math.ceil(rowTotal / pageSize)) {
                return []
            }
            const topReplayFilters = getTopReplayFilter()
            if (!topReplayFilters) {
                return []
            }
            const quickFilters: FilterCommonCondition[] = [...topReplayFilters]
            const payload = {
                stackId,
                pageId,
                scope,
                viewId: id,
                uniqueId,
                isSyncComponent: !!scope,
                pagination: { currentPage: pageNum, pageSize },
                params: {
                    filter: stableDeps.resolvedFilter,
                    quickFilters,
                    linkFilter: stableDeps.resolvedLinkFilter,
                    sorts: stableDeps.sorts
                }
            }
            const records = await loadMoreRecords(payload)

            if (records.length === 0) {
                return []
            }

            const tasks = await Promise.allSettled(
                records.map(r => {
                    const rootId = r.content[idFieldId]?.value?.toString()
                    const quickFilterWithRootId = rootId && getFilterByRootId(rootId)
                    if (!quickFilterWithRootId) {
                        throw new Error('find quickFilterWithRootId failed')
                    }
                    const quickFilters: FilterCommonCondition[] = [...quickFilterWithRootId]
                    const replayCountPayload = {
                        stackId,
                        pageId,
                        scope,
                        viewId: id,
                        uniqueId,
                        isSyncComponent: !!scope,
                        pagination: { currentPage: pageNum, pageSize },
                        params: {
                            filter: stableDeps.resolvedFilter,
                            quickFilters,
                            linkFilter: stableDeps.resolvedLinkFilter,
                            sorts: stableDeps.sorts
                        }
                    }
                    return loadCommentViewReplayCount(replayCountPayload).then(res => (res ? { id: r.id, count: res } : undefined))
                })
            )
            setReplayCountMap(draft => {
                tasks.forEach(item => {
                    if (item.status === 'fulfilled' && item.value) {
                        draft[item.value.id] = item.value.count
                    }
                })
            })

            return records
        },
        [
            rowTotal,
            pageSize,
            getTopReplayFilter,
            stackId,
            pageId,
            scope,
            id,
            uniqueId,
            stableDeps.resolvedFilter,
            stableDeps.resolvedLinkFilter,
            stableDeps.sorts,
            loadMoreRecords,
            setReplayCountMap,
            idFieldId,
            getFilterByRootId,
            loadCommentViewReplayCount
        ]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    useConfigure({
        blockData,
        fetchViewData
    })

    const store = useStore()

    // 评论记录变化时，更新视图记录
    useEffect(() => {
        if (!pointer) {
            return
        }
        const { subscribeId, unSubscribe } = pageStackPubSub.subscribe(`${pointer}-ADD`, (record: RecordLikeProtocol) => {
            setViewIndependentData(draft => {
                draft.rowTotal = draft.rowTotal ? draft.rowTotal + 1 : 1
                if (!commentConfig?.rootId) {
                    return
                }

                const rootIdValue = record.content[commentConfig.rootId]?.value
                // 如果新增的record有commentConfig.rootId，则找到对应的rootId下的记录，插入到最后一位，否则插入到第一位
                if (rootIdValue) {
                    // 有 rootId，插入到对应 rootId 子级列表最后一位
                    // 这里假设 record.content[commentConfig.rootId].value 是父评论的 id
                    // 需要找到所有属于该 rootId 的记录，将新记录插入到这些记录的最后
                    // 假设子评论是连续排列的，找到最后一个属于该 rootId 的记录，插入到其后
                    // 找到所有属于该 rootId 的记录的索引
                    const recordPool = store.get(recordPoolAtom)
                    const childIndexes = draft.recordPoints
                        .map((point, idx) => {
                            const rec = findRecordInPool(recordPool, pointer, point.id)
                            if (rec && rec.content?.[commentConfig.rootId]?.value === rootIdValue) {
                                return idx
                            }
                            return -1
                        })
                        .filter(idx => idx !== -1)
                    if (childIndexes.length > 0) {
                        // 插入到最后一个子评论的后面
                        const lastIdx = childIndexes[childIndexes.length - 1]
                        draft.recordPoints = [
                            ...draft.recordPoints.slice(0, lastIdx + 1),
                            { id: record.id },
                            ...draft.recordPoints.slice(lastIdx + 1)
                        ]
                    } else {
                        // 没有找到子评论，直接插入到最后
                        draft.recordPoints = [...draft.recordPoints, { id: record.id }]
                    }
                } else {
                    // 没有 rootId，插入到一级列表的第一位
                    draft.recordPoints = [{ id: record.id }, ...draft.recordPoints]
                }
            })
        })
        return () => unSubscribe(subscribeId)
    }, [commentConfig?.rootId, fetchViewData, pointer, setViewIndependentData, store])

    const handleViewRefresh = useCallback(() => {
        fetchViewData({ pageNum: 1 })
    }, [fetchViewData])

    const handleLoadMore = useCallback(() => {
        handleLoadMoreRecords(currentPage + 1)
    }, [currentPage, handleLoadMoreRecords])

    // 扔到上层
    useRegisterViewControlListeners(id, scope, 'view', {
        loadMore: handleLoadMore,
        refresh: handleViewRefresh,
        horizontalScrollTo: (payload: ViewControlHorizontalScrollToAction['params'] | undefined) => {
            const to = payload?.horizontalScrollTo
            const horizontalScrollDirection = payload?.horizontalScrollDirection
            const viewContainer = ref.current
            const items = viewContainer?.querySelectorAll('[data-view-item]')
            const viewItemsWrapper = items?.[0]?.parentElement
            if (!to || !items || !viewItemsWrapper) {
                return
            }
            const viewContentContainer = viewItemsWrapper?.parentElement
            const currentScrollLeft = viewItemsWrapper?.parentElement?.scrollLeft ?? 0
            const itemOffsetWidth = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
            const currentIndex = Math.round(currentScrollLeft / itemOffsetWidth)

            let newIndex

            if (!Number.isNaN(Number(to))) {
                newIndex = horizontalScrollDirection === 'left' ? currentIndex - Number(to) : currentIndex + Number(to)
            }

            if (to === 'end') {
                viewContentContainer &&
                    (horizontalScrollDirection === 'left'
                        ? scroll2EndLeftOrRight(viewContentContainer, { left: 0, behavior: 'smooth' })
                        : scroll2EndLeftOrRight(viewContentContainer, { left: viewItemsWrapper.clientWidth, behavior: 'smooth' }))
            }

            if (newIndex === undefined) {
                return
            }
            scroll2ViewItem({ id, scope }, newIndex, { block: 'nearest', inline: 'nearest' })
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    return useMemo(() => {
        if (!pointer || !dataSource) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_datasource.svg')} alt="请在「右侧栏>配置」中选择数据来源" />}
                    description="请在「右侧栏>配置」中选择数据来源"
                />
            )
        }

        if (!commentConfig || !commentConfig.parentId || !commentConfig.rootId) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_content.svg')} alt="请在「右侧栏>配置」中选择评论层级结构" />}
                    description="请在「右侧栏>配置」中选择评论层级结构"
                />
            )
        }

        // 首次加载未完成时（包括请求未开始和请求进行中），不渲染内容，避免闪现"暂无记录"
        if (firstRef.current && !fetchViewRecordsValue) {
            return null
        }
        // 首次加载完成，标记为非首次
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: baseBreakpoint.size.overflow,
                    ...rest.style,
                    ...(viewType === 'custom'
                        ? { ...getPaddingStyle(baseBreakpoint.viewLayout?.viewPadding, true), ...getCursorStyle(appId, cursor) }
                        : {})
                }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockProvider
                            uniqueId={uniqueId}
                            blockData={blockData}
                            dataSource={dataSource}
                            recordPoints={recordPoints}
                            recordPoolAtom={recordPoolAtom}
                            dataSourceList={dataSourceList}
                        >
                            <CustomCommentBlock
                                key={enforceUpdateKey}
                                node={node}
                                breakPoint={breakPoint}
                                pagination={mergedPagination}
                                replayCountMap={replayCountMap}
                                onRenderRichText={handleRenderRichText}
                                onLoadReplayData={fetchReplayData}
                                onLoadMoreData={handleLoadMoreRecords}
                                onRecordClick={handleRecordClickEvent}
                            />
                        </ViewBlockProvider>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        commentConfig,
        firstRef,
        fetchViewRecordsValue,
        id,
        title,
        viewType,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        baseBreakpoint.viewLayout?.viewPadding,
        rest,
        appId,
        cursor,
        ref,
        breakPoint,
        uniqueId,
        blockData,
        recordPoints,
        dataSourceList,
        enforceUpdateKey,
        node,
        mergedPagination,
        replayCountMap,
        handleRenderRichText,
        fetchReplayData,
        handleLoadMoreRecords,
        handleRecordClickEvent,
        children
    ])
}

export default CustomCommentController
